import { SchemaSection } from '@rossum/api-client/schemas';
import type { ComputedFieldDraft } from '../../../../chat/types';
import { findSectionIdForField } from './utils';

type FieldDraftInput = {
  fieldSchemaId: string;
  label: string;
  schemaContent: SchemaSection[];
  /** The lookup configuration as stored, i.e. `matching.configuration`. */
  configuration: Record<string, unknown>;
};

/**
 * Shapes the computed field for the agent. Called when the user opens the agent on the field, so
 * the chat captures the unsaved form state rather than the saved schema.
 *
 * Takes the field's values rather than the form's `control`: the shell already watches all three
 * to render the editor, so re-watching them here would duplicate those subscriptions and put a
 * `Control` on every editor variant's props just to reach one of them.
 *
 * A plain function, not a hook — the result is read once, in a click handler, so memoising it
 * would only pay `findSectionIdForField` on every keystroke in the fields form.
 */
export const getFieldDraft = ({
  fieldSchemaId,
  label,
  schemaContent,
  configuration,
}: FieldDraftInput): ComputedFieldDraft => {
  const { dataset } = configuration;

  return {
    kind: 'lookup',
    field_schema_id: fieldSchemaId,
    label,
    section_id: findSectionIdForField(schemaContent, fieldSchemaId),
    schema_content: schemaContent,
    dataset: typeof dataset === 'string' ? dataset : null,
    configuration,
  };
};
